<template>
  <div
    v-if="selectedPlan"
    class="bg-white rounded-[10px] p-5 border border-[#ebebeb]"
  >
    <!-- Title -->
    <div class="text-[16px] font-medium text-[#171717] mb-4">
      Configuration summary
    </div>

    <!-- Selected Tier -->
    <div>
      <div class="text-[10px] font-normal text-[#a1a1a1] uppercase tracking-wider mb-2">
        Selected Tier
      </div>
      <div class="flex justify-between items-baseline mb-3">
        <span class="text-[14px] font-medium text-[#171717]">
          {{ selectedPlan.name | nt }}
        </span>
        <span class="text-[14px] font-medium text-[#171717]">
          {{ formatPlanPrice }}<span class="text-[12px] font-normal text-[#a1a1a1]">/{{ periodAbbr }}</span>
        </span>
      </div>
      <!-- MAU / Data points box -->
      <div class="rounded-[4px] bg-[#fafafa] px-[10px] py-[6px] border border-[#ebebeb]">
        <div class="flex justify-between items-center text-[12px] font-normal mb-[2px]">
          <span class="text-[#737373]">Monthly active users</span>
          <span class="text-[#171717]">{{ formatNumber(planMau) }}</span>
        </div>
        <div class="flex justify-between items-center text-[12px] font-normal">
          <span class="text-[#737373]">Data points</span>
          <span class="text-[#171717]">{{ formatNumber(planMau * 1000) }}</span>
        </div>
      </div>
    </div>

    <!-- Capabilities (Bundles + Feature Add-ons) -->
    <template v-if="selectedBundles.length || selectedFeatures.length">
      <div class="border-t border-dashed border-cool-gray-30 my-4" />
      <div class="text-[10px] font-normal text-[#a1a1a1] uppercase tracking-wider mb-2">
        Capabilities
      </div>
      <div
        v-for="(bundle, index) in selectedBundles"
        :key="'bundle-' + index"
        class="flex justify-between items-baseline mb-2"
      >
        <span class="text-[12px] font-medium text-[#171717]">{{ bundle.name | nt }}</span>
        <span class="text-[12px] font-normal text-[#171717]">
          {{ priceFormatter(bundle.pricing[period].price, true) }}<span class="text-[#a1a1a1]">/{{ periodAbbr }}</span>
        </span>
      </div>
      <div
        v-for="(feat, index) in selectedFeatures"
        :key="'feat-' + index"
        class="flex justify-between items-baseline mb-2"
      >
        <span class="text-[12px] font-medium text-[#171717]">{{ feat.name | nt }}</span>
        <span class="text-[12px] font-normal text-[#171717]">
          {{ priceFormatter(feat.pricing[period].price, true) }}<span class="text-[#a1a1a1]">/{{ periodAbbr }}</span>
        </span>
      </div>
    </template>

    <!-- Hosting Region -->
    <template v-if="selectedRegion">
      <div class="border-t border-dashed border-cool-gray-30 my-4" />
      <div class="text-[10px] font-normal text-[#a1a1a1] uppercase tracking-wider mb-2">
        Hosting Region
      </div>
      <div class="flex justify-between items-baseline mb-2">
        <span class="text-[12px] font-medium text-[#171717]">{{ selectedRegion.name | nt }}</span>
        <span
          v-if="isNativePriceFree(selectedRegion.pricing[period].price)"
          class="text-[12px] font-normal text-[#10a14a]"
        >Free</span>
        <span
          v-else
          class="text-[12px] font-normal text-[#171717]"
        >{{ formatRegionPrice }}<span class="text-[#a1a1a1]">/{{ periodAbbr }}</span></span>
      </div>
    </template>

    <!-- Server (Support + Retention + Custom domain) -->
    <template v-if="supportLevel || selectedRetention || showPaidDomain">
      <div class="border-t border-dashed border-cool-gray-30 my-4" />
      <div class="text-[10px] font-normal text-[#a1a1a1] uppercase tracking-wider mb-2">
        Server
      </div>
      <div
        v-if="supportLevel"
        class="flex justify-between items-baseline mb-2"
      >
        <span class="text-[12px] font-medium text-[#171717]">
          {{ isDedicatedSupport ? 'Dedicated support' : 'Standard support' }}
        </span>
        <span
          v-if="isDedicatedSupport"
          class="text-[12px] font-normal text-[#171717]"
        >+{{ dedicatedSupportPriceFormatted }}<span class="text-[#a1a1a1]">/{{ periodAbbr }}</span></span>
        <span
          v-else
          class="text-[12px] font-normal text-[#10a14a]"
        >Free</span>
      </div>
      <div
        v-if="selectedRetention"
        class="flex justify-between items-baseline mb-2"
      >
        <span class="text-[12px] font-medium text-[#171717]">{{ selectedRetention.name | nt }}</span>
        <span
          v-if="isNativePriceFree(selectedRetention.pricing[period].price)"
          class="text-[12px] font-normal text-[#10a14a]"
        >Free</span>
        <span
          v-else
          class="text-[12px] font-normal text-[#171717]"
        >{{ priceFormatter(selectedRetention.pricing[period].price, true) }}<span class="text-[#a1a1a1]">/{{ periodAbbr }}</span></span>
      </div>
      <div
        v-if="showPaidDomain"
        class="flex justify-between items-baseline mb-2"
      >
        <span class="text-[12px] font-medium text-[#171717]">{{ selectedDomain.name.split('_')[0] | nt }}</span>
        <span class="text-[12px] font-normal text-[#171717]">+{{ priceFormatter(selectedDomain.pricing[period].price, true) }}<span class="text-[#a1a1a1]">/{{ periodAbbr }}</span></span>
      </div>
    </template>

    <!-- One-Time Services -->
    <template v-if="activeOneTimeServices.length">
      <div class="border-t border-dashed border-cool-gray-30 my-4" />
      <div class="text-[10px] font-normal text-[#a1a1a1] uppercase tracking-wider mb-2">
        One-Time Services
      </div>
      <div
        v-for="service in activeOneTimeServices"
        :key="service.id"
        class="flex justify-between items-baseline mb-2"
      >
        <span class="text-[12px] font-medium text-[#171717]">{{ service.title | nt }}</span>
        <span class="text-[12px] font-normal text-[#171717]">{{ formatCurrency(service.price) }}</span>
      </div>
    </template>

    <!-- Dashed separator before totals -->
    <div class="border-t border-dashed border-cool-gray-30 my-4" />

    <!-- Monthly Subscription -->
    <div class="flex justify-between items-baseline mb-1">
      <span class="text-[10px] font-normal text-[#a1a1a1] uppercase tracking-wider">
        {{ period === 'year' ? 'Yearly' : 'Monthly' }} Subscription
      </span>
      <span class="text-base font-semibold text-cool-gray-100">
        <template v-if="recurringMonthlyTotal > 0">
          {{ formatCurrency(recurringMonthlyTotal) }}<span class="text-sm font-normal text-cool-gray-50">/{{ periodAbbr }}</span>
        </template>
        <template v-else>included</template>
      </span>
    </div>

    <!-- One-Time Fees -->
    <div
      v-if="oneTimeTotal > 0"
      class="flex justify-between items-baseline mb-1"
    >
      <span class="text-[10px] font-normal text-[#a1a1a1] uppercase tracking-wider">
        One-Time Fees
      </span>
      <span class="text-base font-semibold text-cool-gray-100">
        {{ formatCurrency(oneTimeTotal) }}
      </span>
    </div>

    <!-- Solid separator -->
    <div class="border-t-2 border-cool-gray-100 my-4" />

    <!-- Total due / Due today -->
    <div class="flex justify-between items-baseline mb-4">
      <span class="text-lg font-semibold text-cool-gray-100">
        {{ hasTrial
          ? $t('mycountly.server-creation.summary-due-today')
          : $t('mycountly.server-creation.summary-total-due') }}
      </span>
      <div class="flex flex-col items-end">
        <span
          class="text-[30px] font-semibold"
          :class="hasTrial && oneTimeTotal === 0 ? 'text-[#10A14A]' : 'text-[#171717]'"
        >
          {{ grandTotalAmount }}
        </span>
        <span
          v-if="grandTotalAmount !== '$0'"
          class="text-xs text-cool-gray-50"
        >
          {{ $t('mycountly.server-creation.summary-plus-taxes') }}
        </span>
      </div>
    </div>

    <!-- Trial footer — only when selected tier has trial -->
    <div
      v-if="hasTrial"
      class="border-t border-cool-gray-20 pt-3"
    >
      <div class="text-sm text-cool-gray-50 text-center">
        <template v-if="recurringMonthlyTotal > 0">
          {{ $t('mycountly.server-creation.summary-after-trial', {
            price: formatCurrency(recurringMonthlyTotal),
            period: periodAbbr,
          }) }} {{ $t('mycountly.server-creation.summary-plus-taxes') }}
        </template>
        <template v-else>
          {{ $t('mycountly.server-creation.summary-after-trial-included') }}
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, storeToRefs } from 'pinia';
import { useServerCreationStore } from '@/stores';
import { featureCodeMatches } from '@/stores/server-creation';
import { DeploymentHelper } from '@/utils/mixins/deployment-helper';
import { FeatureStatus, FeatureTypes } from '@/utils/server-creation/types/server-creation-types';

export default {
  mixins: [DeploymentHelper],
  setup() {
    const serverCreationStore = useServerCreationStore();
    const { configurables, plans } = storeToRefs(serverCreationStore);

    return {
      plans,
      configurables,
    };
  },
  computed: {
    ...mapState(useServerCreationStore, ['serverCreationState']),
    period() {
      return this.serverCreationState.data.activePaymentPeriod;
    },
    periodAbbr() {
      if (this.period === 'month') return 'mo.';
      if (this.period === 'year') return 'yr.';
      return this.period;
    },
    planMau() {
      if (!this.selectedPlan) return 0;
      return this.extractMauFromPlanCode(this.selectedPlan.code);
    },
    selectedPlan() {
      if (this.serverCreationState.data.isLoading) return null;
      return this.plansByProvider.find(
        (plan) => plan.code === this.serverCreationState.data.subscription.planCode,
      );
    },
    plansByProvider() {
      if (this.serverCreationState.data.isLoading) return [];
      return this.plans.data
        .filter(
          (plan) => typeof plan.providers.find(
            (p) => p.refId === this.serverCreationState.data.subscription.providerId,
          ) !== 'undefined',
        );
    },
    selectedRegion() {
      if (!this.selectedPlan) return null;
      const regionId = this.serverCreationState.data.subscription.planConfig.region;
      if (!regionId) return null;

      const region = this.configurables.data.regions.find((r) => r._id === regionId);
      const planRegion = this.selectedPlan.regions.find((r) => r.refId === regionId);
      if (!region || !planRegion) return null;

      return {
        ...region,
        pricing: planRegion.pricing,
      };
    },
    selectedBundles() {
      const bundleIds = this.serverCreationState.data.subscription.planConfig.bundles || [];
      if (!this.selectedPlan || !bundleIds.length) return [];

      const planBundlesById = (this.selectedPlan.bundles || []).reduce((acc, pb) => {
        if (pb.refId) acc[pb.refId.toString()] = pb;
        return acc;
      }, {});

      const allBundles = this.configurables.data.bundles || [];
      return bundleIds
        .map((id) => {
          const bundleDoc = allBundles.find(
            (b) => b._id && b._id.toString() === id.toString(),
          );
          const planBundle = planBundlesById[id.toString()];
          if (!bundleDoc || !planBundle) return null;
          return {
            name: bundleDoc.meta?.catalogTitle || bundleDoc.name,
            pricing: planBundle.pricing,
          };
        })
        .filter(Boolean);
    },
    featuresByPlan() {
      const plan = this.plans.data.find(
        (p) => p.code === this.serverCreationState.data.subscription.planCode,
      );
      const features = [];

      if (plan && this.configurables?.data?.features?.length) {
        for (let i = 0; i < this.configurables.data.features.length; i += 1) {
          for (let j = 0; j < plan.features.length; j += 1) {
            if (this.configurables.data.features[i]._id === plan.features[j].refId) {
              features.push({
                ...this.configurables.data.features[i],
                pricing: plan.features[j].pricing,
                disabled: plan.features[j].status !== FeatureStatus.AVAILABLE,
              });
            }
          }
        }

        return {
          feature: features.filter((f) => f.type === FeatureTypes.FEATURE),
          retention: features.filter((f) => f.type === FeatureTypes.RETENTION),
          domain: features.filter((f) => f.type === FeatureTypes.DOMAIN),
          support: features.filter((f) => f.type === FeatureTypes.SUPPORT),
          oneTime: features.filter((f) => f.type === FeatureTypes.ONE_TIME_SERVICE),
        };
      }

      return {
        feature: [], retention: [], domain: [], support: [], oneTime: [],
      };
    },
    selectedFeatures() {
      const featureIds = this.serverCreationState.data.subscription.planConfig.features || [];
      if (!featureIds.length) return [];
      return this.featuresByPlan.feature.filter((f) => featureIds.includes(f._id));
    },
    selectedDomain() {
      const domainId = this.serverCreationState.data.subscription.specialFeatures?.domain;
      if (!domainId || !this.selectedPlan) return null;

      const domain = this.featuresByPlan.domain.find((f) => f._id === domainId);
      const planFeature = this.selectedPlan.features.find((r) => r.refId === domainId);
      if (!domain || !planFeature) return null;

      return { ...domain, pricing: planFeature.pricing };
    },
    showPaidDomain() {
      if (!this.selectedDomain) return false;
      const price = this.selectedDomain.pricing?.[this.period]?.price;
      if (!price) return false;
      return !this.isNativePriceFree(price);
    },
    selectedRetention() {
      const retentionId = this.serverCreationState.data.subscription.specialFeatures?.retention;
      if (!retentionId || !this.selectedPlan) return null;

      const retention = this.featuresByPlan.retention.find((f) => f._id === retentionId);
      const planFeature = this.selectedPlan.features.find((r) => r.refId === retentionId);
      if (!retention || !planFeature) return null;

      return { ...retention, pricing: planFeature.pricing };
    },
    formatPlanPrice() {
      if (!this.selectedPlan?.basePricing?.[this.period]) return '';
      return this.priceFormatter(this.selectedPlan.basePricing[this.period].price, true);
    },
    formatRegionPrice() {
      if (!this.selectedRegion?.pricing?.[this.period]) return '';
      return this.priceFormatter(this.selectedRegion.pricing[this.period].price, true);
    },
    recurringMonthlyTotal() {
      if (!this.selectedPlan) return 0;
      const { period } = this;
      const extractAmount = (priceObj) => {
        const amt = priceObj?.amount;
        if (amt == null) return 0;
        if (typeof amt === 'object' && amt.$numberDecimal != null) return Number(amt.$numberDecimal);
        return Number(amt) || 0;
      };

      let total = extractAmount(this.selectedPlan.basePricing?.[period]?.price);
      this.selectedBundles.forEach((b) => { total += extractAmount(b.pricing?.[period]?.price); });
      this.selectedFeatures.forEach((f) => { total += extractAmount(f.pricing?.[period]?.price); });
      if (this.selectedRegion) total += extractAmount(this.selectedRegion.pricing?.[period]?.price);
      if (this.selectedRetention) total += extractAmount(this.selectedRetention.pricing?.[period]?.price);
      if (this.showPaidDomain) total += extractAmount(this.selectedDomain.pricing?.[period]?.price);
      if (this.isDedicatedSupport) total += this.dedicatedSupportPrice;

      return total;
    },
    supportLevel() {
      return this.serverCreationState.data.supportLevel || null;
    },
    isDedicatedSupport() {
      return this.supportLevel === 'dedicated';
    },
    dedicatedSupportFeature() {
      return this.featuresByPlan.support?.find((f) => featureCodeMatches(f.code, 'support-dedicated')) || null;
    },
    dedicatedSupportPrice() {
      if (!this.dedicatedSupportFeature?.pricing?.[this.period]?.price?.amount) return 0;
      const amt = this.dedicatedSupportFeature.pricing[this.period].price.amount;
      return typeof amt === 'object' && amt?.$numberDecimal != null ? Number(amt.$numberDecimal) : Number(amt);
    },
    dedicatedSupportPriceFormatted() {
      if (this.dedicatedSupportPrice === 0) return '$0';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(this.dedicatedSupportPrice);
    },
    activeOneTimeServices() {
      const serviceIds = this.serverCreationState.data.oneTimeServices || [];
      if (!serviceIds.length) return [];
      return this.featuresByPlan.oneTime
        .filter((f) => serviceIds.includes(f.code))
        .map((f) => {
          const amt = f.pricing?.month?.price?.amount;
          const price = typeof amt === 'object' && amt?.$numberDecimal != null ? Number(amt.$numberDecimal) : Number(amt || 0);
          return {
            id: f.code,
            title: f.meta?.catalogTitle || f.name || f.code,
            price,
          };
        });
    },
    oneTimeTotal() {
      return this.activeOneTimeServices.reduce((sum, s) => sum + s.price, 0);
    },
    hasTrial() {
      return !!this.selectedPlan?.paddleTrialPeriod;
    },
    monthlySubscriptionTotal() {
      const { pricing } = this.serverCreationState.data;
      if (!pricing || pricing.total === undefined || pricing.total === null) return 0;
      const raw = pricing.total;
      if (typeof raw === 'object' && raw?.$numberDecimal != null) return Number(raw.$numberDecimal);
      return Number(raw) || 0;
    },
    grandTotalAmount() {
      if (this.hasTrial) {
        return this.oneTimeTotal > 0 ? this.formatCurrency(this.oneTimeTotal) : '$0';
      }
      const total = this.monthlySubscriptionTotal + this.oneTimeTotal + (this.isDedicatedSupport ? this.dedicatedSupportPrice : 0);
      return total > 0 ? this.formatCurrency(total) : '$0';
    },
  },
  methods: {
    formatNumber(num) {
      return num.toLocaleString('en-US');
    },
    isNativePriceFree(price) {
      return price && price.amount && price.amount.$numberDecimal === '0';
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    },
    priceFormatter(price, isNative = false) {
      if (
        (isNative && price.amount.$numberDecimal === '0')
        || (!isNative && price.total === 0)
      ) {
        return 'included';
      }

      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });

      if (isNative) {
        return formatter.format(price.amount.$numberDecimal);
      }

      return formatter.format(price.total);
    },
  },
};
</script>
